import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, rufe } from "../api/client";
import type { buchungDetail, buchungenListe } from "../../worker/data/buchungen";
import type { BuchungEingabe } from "@shared/schemas/stammdaten";

export type { BuchungEingabe };
export type BuchungDetail = Awaited<ReturnType<typeof buchungDetail>>;
export type BuchungZeile = Awaited<ReturnType<typeof buchungenListe>>[number];

const K = ["laden", "buchungen"] as const;

function nachAenderung() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: ["laden"] });
}

export const useBuchungen = (status?: string) => useQuery({ queryKey: [...K, status ?? ""], queryFn: () => rufe<{ buchungen: BuchungZeile[] }>(api.api.laden.buchungen.$get({ query: { status } })) });
export const useBuchung = (id: string) => useQuery({ queryKey: [...K, id], queryFn: () => rufe<BuchungDetail>(api.api.laden.buchungen[":id"].$get({ param: { id } })), enabled: !!id });

export function useBuchungAnlegen() {
  const nach = nachAenderung();
  return useMutation({ mutationFn: (d: BuchungEingabe) => rufe<{ id: string; nummer: string; status: string }>(api.api.laden.buchungen.$post({ json: d })), onSuccess: nach });
}
export function useBuchungAktion() {
  const nach = nachAenderung();
  return useMutation({
    mutationFn: (v: { id: string; aktion: "link" | "zustimmung-vor-ort" | "verlaengern" | "kuendigen" | "stornieren" | "kaution"; daten?: Record<string, unknown> }) => {
      const b = api.api.laden.buchungen[":id"];
      const param = { id: v.id };
      switch (v.aktion) {
        case "link":
          return rufe(b.link.$post({ param }));
        case "zustimmung-vor-ort":
          return rufe(b["zustimmung-vor-ort"].$post({ param }));
        case "verlaengern":
          return rufe(b.verlaengern.$post({ param, json: { dauer: Number(v.daten?.dauer ?? 1) } }));
        case "kuendigen":
          return rufe(b.kuendigen.$post({ param, json: { zum: (v.daten?.zum as string | undefined) || undefined, notiz: String(v.daten?.notiz ?? "") } }));
        case "stornieren":
          return rufe(b.stornieren.$post({ param, json: { grund: String(v.daten?.grund ?? "") } }));
        case "kaution":
          return rufe(b.kaution.$post({ param, json: { art: v.daten?.art as "bar" | "ueberweisung", am: (v.daten?.am as string | undefined) || undefined } }));
      }
    },
    onSuccess: nach,
  });
}

export function vertragPdfUrl(id: string): string {
  return `/api/laden/buchungen/${id}/vertrag.pdf`;
}

// --- Einrichtung ---------------------------------------------------------------------------------

export interface EinrichtungStand {
  erledigt: ("laden" | "preise" | "regale" | "sumup" | "personal")[];
  abgeschlossenAt: string | null;
  stand: { laden: boolean; preise: boolean; regale: number; sumup: boolean; personal: number };
}
export const useEinrichtung = () => useQuery({ queryKey: ["laden", "einrichtung"], queryFn: () => rufe<EinrichtungStand>(api.api.laden.einrichtung.$get()) });
export function useEinrichtungSchritt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { schritt: EinrichtungStand["erledigt"][number]; erledigt?: boolean }) => rufe(api.api.laden.einrichtung.schritt.$post({ json: { schritt: v.schritt, erledigt: v.erledigt ?? true } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["laden", "einrichtung"] }),
  });
}
export function useEinrichtungAbschliessen() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: () => rufe(api.api.laden.einrichtung.abschliessen.$post()), onSuccess: () => void qc.invalidateQueries({ queryKey: ["laden"] }) });
}
